import React from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * Action — uniform way to declare page-level actions.
 *
 * PageHeader reads `__isAction` to know which children are Actions so it can
 * move secondary ones into the mobile overflow menu.
 *
 * Props:
 *   primary   {boolean}  — primary action. Renders as an outlined button on
 *                          desktop and a compact icon-first control on mobile.
 *   icon      {node}     — icon element
 *   label     {string}   — button label / tooltip text (required)
 *   onClick   {function}
 *   disabled  {boolean}
 *   dividerBefore {boolean} — hint for PageHeader overflow menu to insert a
 *                             Divider above this item
 *   color     {string}   — MUI color token (default 'primary')
 *
 * Usage:
 *   <Action primary icon={<Add />} label="Add invoice" onClick={handleAdd} />
 *   <Action icon={<FileDownload />} label="Export" onClick={handleExport} />
 */
function Action({
  primary = false,
  icon,
  label,
  onClick,
  disabled = false,
  color = 'primary',
  // dividerBefore is intentionally not forwarded to DOM — PageHeader reads it
  // eslint-disable-next-line no-unused-vars
  dividerBefore,
  sx,
  ...rest
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // theme is used for breakpoints above; also available if needed for future sx overrides.

  if (primary && isMobile) {
    return (
      <Tooltip title={label}>
        <span>
          <IconButton
            color={color}
            onClick={onClick}
            disabled={disabled}
            size="small"
            sx={sx}
            {...rest}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  if (primary) {
    return (
      <Button
        variant="outlined"
        color={color}
        startIcon={icon}
        onClick={onClick}
        disabled={disabled}
        sx={sx}
        {...rest}
      >
        {label}
      </Button>
    );
  }

  // Secondary action on desktop → outlined Button
  // (On mobile, PageHeader folds these into the overflow menu instead of
  // rendering this component at all, so we only need the desktop variant.)
  return (
    <Button
      variant="outlined"
      color={color}
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      sx={sx}
      {...rest}
    >
      {label}
    </Button>
  );
}

// Flag used by PageHeader to identify Action children in the actions slot.
Action.__isAction = true;

export default Action;
